import { Injectable } from "@angular/core";

import {
  OrderDataPromiseLookupService,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
  BfxBlotterOrdersContextMenuPromiseService,
  ActiveUserProfilePromiseService,
  BfxBlotterContextMenuItem,
  BfxBlotterAction,
  ContextMenuItemState,
  ContextMenuItem,
  BfxBlotterOrdersContextMenuCancelItem,
  Roles,
} from "shared/cubiq-services";

import { BfxBlotterOrdersActionSubject } from "../bfx-blotter-orders/bfx-blotter-orders-action-subject.module";
import { BfxBlotterOrdersContextMenuService } from "../bfx-blotter-orders/bfx-blotter-orders-contextmenu.service";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import { BfxBlotterOrdersContextMenuPauseItem } from "shared/cubiq-services/model/contextmenu/bfx-blotter-orders/BfxBlotterOrdersContextMenuPauseItem";
import { BfxBlotterActionEvent } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";

const logger = LoggerService.for(__dirname);

const BMALGO_INELIGIBLE_ACTIONS = new Set([
  BfxBlotterAction.ACTION_ORDER_QUICKFILL,
  BfxBlotterAction.ACTION_ORDER_DETAILFILL,
  BfxBlotterAction.ACTION_TRANSFER,
  BfxBlotterAction.ACTION_ABORT_TRANSFER,
  BfxBlotterAction.ACTION_TAKE,
  BfxBlotterAction.ACTION_ORDER_DUPLICATE,
  BfxBlotterAction.ACTION_FORCE_TAKE,
  BfxBlotterAction.ACTION_ORDER_MODIFY,
  BfxBlotterAction.ACTION_ORDER_MODIFY_INFO,
  BfxBlotterAction.ACTION_ORDER_REACTIVATE,
  BfxBlotterAction.ACTION_DI_FILL_APPROVE,
  BfxBlotterAction.ACTION_DI_FILL_CANCEL,
  BfxBlotterAction.ACTION_ORDER_ABORT,
  BfxBlotterAction.ACTION_ORDER_VIEW_TICKET,
  BfxBlotterAction.ACTION_ORDER_RECAP,
  BfxBlotterAction.ACTION_EXPORT_IMPORT,
]);

@Injectable()
export class BfxAlgoMonitorContextMenuService extends BfxBlotterOrdersContextMenuService {
  constructor(
    orderDataService: OrderDataPromiseLookupService,
    systemConfigService: SystemConfigPromiseService,
    traderGroupService: TraderGroupPromiseLookupService,
    activeUserService: ActiveUserProfilePromiseService,
    contextMenuService: BfxBlotterOrdersContextMenuPromiseService,
    // note this is shared with Orders+Inbox(via subclass)+Ladder(via providers in component) + Execution Monitor
    actionSubject: BfxBlotterOrdersActionSubject,
  ) {
    super(
      orderDataService,
      systemConfigService,
      traderGroupService,
      activeUserService,
      contextMenuService,
      actionSubject,
    );
  }

  protected initMenuItems(): void {
    this.menuState.canReactivateOrders = false;
    this.menuState.canViewTicket = true;
  }

  protected reduceVisibleMenuItems<U extends BfxBlotterContextMenuItem<any>>(
    menuItems: U[],
    selectedIds: Array<string> | null = null,
    selectedItems: Array<object> | null = null,
  ): U[] {
    const hasBMAlgos = Array.isArray(selectedItems)
      ? selectedItems.some(
          (item: CubiqTS.OrderDataImpl) => item?.orderType == CubiqTS.OrderType.BMALGO,
        )
      : false;
    if (hasBMAlgos) {
      menuItems = menuItems.filter((menuItem) => !BMALGO_INELIGIBLE_ACTIONS.has(menuItem.actionId));
    }
    return super.reduceVisibleMenuItems(menuItems, selectedIds, selectedItems);
  }

  /**
   * Does user has Pause All privileges? Returns a promise that resolves once menu state
   * is fully initialised
   * @returns
   */
  public canPauseAll(): Promise<boolean> {
    return this.initialised.then(() => this.menuState.canPauseResume);
  }

  /**
   * Does user has Cancel All privileges? Returns a promise that resolves once menu state
   * is fully initialised and is true if either Algo of BMAlgo cancel is allowed
   * @returns
   */
  public canCancelAll(): Promise<boolean> {
    return this.initialised.then(() => this.menuState.canCancel || this.menuState.canCancelHedge);
  }

  /**
   * Does user has Cancel All privileges? Returns a promise that resolves once menu state
   * is fully initialised and is true if Cancel Algo is allowed
   * @returns
   */
  public canCancelAlgos(): Promise<boolean> {
    return this.initialised.then(() => this.menuState.canCancel);
  }

  /**
   * Does user has Cancel All privileges? Returns a promise that resolves once menu state
   * is fully initialised and is true if Cancel BMAlgo is allowed
   * @returns
   */
  public canCancelBMAlgos(): Promise<boolean> {
    return this.initialised.then(() => this.menuState.canCancelHedge);
  }

  /**
   * Extract all Pausable orders form input list and forward these orders to the Pause
   * component. This uses Pause Context Menu to provide order filtering
   *
   * @param orders List of orders, not all of which need to be pausable
   * @returns *true* if at least one order is eligible for Pause
   *    */
  public pauseAllOrders(orders: CubiqTS.OrderDataImpl[]): boolean {
    const pauseAction = new BfxBlotterOrdersContextMenuPauseItem();
    return this.executeAggregateOrderAction(orders, pauseAction);
  }

  /**
   * Extract all Cancelable orders form input list and forward these orders to the Cancel
   * component. This uses Cancel Context Menu to provide order filtering
   *
   * @param orders List of orders, not all of which need to be cancelable
   * @returns *true* if at least one order is eligible for Cancel
   */
  public cancelAllOrders(orders: CubiqTS.OrderDataImpl[]): boolean {
    const cancelAction = new BfxBlotterOrdersContextMenuCancelItem();
    return this.executeAggregateOrderAction(orders, cancelAction);
  }

  /**
   * Finds all orders are that enabled for the specified context menu item, and send them off
   * with the specified menu action. Common for Pausing and cancel
   *
   *
   * @param orders List of orders, not all of which need to be actionable
   * @param menuItem
   * @returns *true* if at least one order has been actioned
   */
  private executeAggregateOrderAction(
    orders: CubiqTS.OrderDataImpl[],
    menuItem: BfxBlotterContextMenuItem<CubiqTS.OrderDataImpl>,
  ): boolean {
    const actionableOrders: CubiqTS.OrderDataImpl[] = [];
    const itemState = new ContextMenuItemState();

    orders.forEach((order) => {
      // Check each order for eligibility
      itemState.count = 0;
      itemState.enabled = true; // Needed as Cancels assumes init value is true
      menuItem.checkEnabled(this.menuState, itemState, order);
      if (itemState.enabled) {
        actionableOrders.push(order);
      }
    });
    if (actionableOrders.length > 0) {
      // Execute the action will all eligible orders
      const actionableOrderIds = actionableOrders.map((order) => order.orderId.toString());
      let actionEvent = new BfxBlotterActionEvent(
        menuItem.actionId,
        null,
        actionableOrderIds,
        actionableOrders,
      );
      this.actionSubject.next(actionEvent);
      return true;
    }
    return false;
  }
}
